
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    DropdownMenu, Dropdown, DropdownItem, DropdownToggle, Label, FormGroup
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import TableContainer from "../../Components/Common/TableContainer";
import { useNavigate } from "react-router-dom";
import { Toast, ToastBody, ToastHeader } from "reactstrap";
import { useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";


interface Client {
    rowid: string;
    nom: string;
    address?: string;
    email?: string;
    phone?: string;
    outstanding_limit?: string;
    town?: string | null;
    zip?: string | null;
}

interface Propal {
    rowid: string;
    ref: string;
    date_propale: string;
    name: string;
    total_ht: string;
    total_ttc: string;
    fk_statut: string;
    client_email?: string;
    client_phone?: string;
    client_address?: string;
    billed?: string;
}

interface Order {
    orderid: string;
    ref: string;
    ref_client: string | null;
    date_facture: number;
    total_ht: string;
    total_ttc: string;
}

interface PropalDetails {
    id: string;
    ref: string;
    client: string;
    total_ht: string;
    total_ttc: string;
    datep: number;
    fk_statut: string;
    type: string;
    propalpdf: string;
    orders: Order[];
    orderpdf: string;
    lines: Array<{
        id: string;
        product_label: string;
        product_ref: string;
        subprice: string;
        qty: string;
        tva_tx: string;
        total_ht: string;
        total_tva: string;
        total_ttc: string;
        remise_percent: string;
        product_type: string;
        fk_product: string;
        brand_id: string;
        brand: string;
        warehouse?: string | null;
    
    }>;
    thirdparty: {
        name: string;
        address?: string;
        email?: string;
        phone?: string;
        rowid: string;
    };
}

interface SmartPaginationProps {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    countPerPage: number;
    onPageChange: (page: number) => void;
    loading: boolean;
}

const SmartPagination: React.FC<SmartPaginationProps> = ({
    currentPage,
    totalPages,
    totalCount,
    countPerPage,
    onPageChange,
    loading
}) => {
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    const [pageInput, setPageInput] = useState(currentPage.toString());

    useEffect(() => {
        setPageInput(currentPage.toString());
    }, [currentPage]);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    const handlePageJump = () => {
        const newPage = parseInt(pageInput);
        if (!isNaN(newPage)) {
            const clampedPage = Math.max(1, Math.min(totalPages, newPage));
            onPageChange(clampedPage);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePageJump();
        }
    };

    return (
        <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
                Affichage <strong>{countPerPage}</strong>
            </div>

            <Pagination className="mb-0">
                <PaginationItem disabled={currentPage === 1 || loading}>
                    <PaginationLink first onClick={() => onPageChange(1)} />
                </PaginationItem>
                <PaginationItem disabled={currentPage === 1 || loading}>
                    <PaginationLink previous onClick={() => onPageChange(currentPage - 1)} />
                </PaginationItem>

                {startPage > 1 && (
                    <>
                        <PaginationItem disabled={loading}>
                            <PaginationLink onClick={() => onPageChange(1)}>1</PaginationLink>
                        </PaginationItem>
                        {startPage > 2 && (
                            <PaginationItem disabled>
                                <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                        )}
                    </>
                )}

                {pages.map(page => (
                    <PaginationItem active={page === currentPage} key={page} disabled={loading}>
                        <PaginationLink onClick={() => onPageChange(page)}>
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && (
                            <PaginationItem disabled>
                                <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                        )}
                        <PaginationItem disabled={loading}>
                            <PaginationLink onClick={() => onPageChange(totalPages)}>
                                {totalPages}
                            </PaginationLink>
                        </PaginationItem>
                    </>
                )}

                <PaginationItem disabled={currentPage === totalPages || loading}>
                    <PaginationLink next onClick={() => onPageChange(currentPage + 1)} />
                </PaginationItem>
                <PaginationItem disabled={currentPage === totalPages || loading}>
                    <PaginationLink last onClick={() => onPageChange(totalPages)} />
                </PaginationItem>
            </Pagination>

            <div className="d-flex align-items-center gap-1">
                <Input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '60px' }}
                    className="text-center"
                    bsSize="sm"
                    disabled={loading}
                />
                <Button
                    color="primary"
                    size="sm"
                    onClick={handlePageJump}
                    disabled={loading}
                >
                    {loading ? <Spinner size="sm" /> : 'Aller'}
                </Button>
            </div>
        </div>
    );
};

const PropalsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [propals, setPropals] = useState<Propal[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [searchRef, setSearchRef] = useState("");
    const [searchDateStart, setSearchDateStart] = useState("");
    const [searchDateEnd, setSearchDateEnd] = useState("");
    const [searchProduct, setSearchProduct] = useState("");
    const[searchProductRef,setSearchProductRef]=useState("");
    const [clientId, setClientId] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [propalDetails, setPropalDetails] = useState<PropalDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [countPerPage, setCountPerPage] = useState(0);
    const [clientSearchTerm, setClientSearchTerm] = useState("");
    const [fetchedClients, setFetchedClients] = useState<Client[]>([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientFetchError, setClientFetchError] = useState<string | null>(null);
    const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
    const [enCompte, setEnCompte] = useState<boolean>(false);
    const [comptoir, setComptoir] = useState<boolean>(false);
    const [clientName, setClientName] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [editedLines, setEditedLines] = useState<PropalDetails['lines']>([]);
    const [deletedLineIds, setDeletedLineIds] = useState<string[]>([]);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastColor, setToastColor] = useState('info');
    const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
    const [propalPdf, setPropalPdf] = useState<{ propalPdf: string ;orderpdf :string} | null>(null);
    const [orderPdf, setOrderPdf] = useState<{ orderpdf: string } | null>(null);
    const [pdfModalOpen, setPdfModalOpen] = useState(false); 
    const [pdfType, setPdfType] = useState<'propal' | 'commande'>('propal');
    const [searchStatus, setSearchStatus] = useState("");

    const tableStyles = {
        actionCell: {
            width: '80px',
            textAlign: 'center' as const,
            verticalAlign: 'middle'
        },
        quantityCell: {
            textAlign: 'center' as const,
            verticalAlign: 'middle'
        },
        quantityInput: {
            width: '70px',
            margin: '0 auto',
            textAlign: 'center' as const
        },
        deleteButton: {
            display: 'block',
            margin: '0 auto',
            width: '30px',
            height: '30px',
            padding: '0',
            alignItems: 'center' as const,
            justifyContent: 'center' as const
        }
    };

    const fetchPropals = async () => {
        setLoading(true);
        
        try {
            const params: any = {
                action: 'propals',
                entity: user.entity,
                page: page,
                limit: 100,
                sortfield: 'p.datep',
                sortorder: 'DESC',
            };

            // Ajouter les paramètres de recherche
            if (searchRef) {
                params.search_ref = searchRef;
            }
            if( searchProduct) {
                params.search_product_id = searchProduct;}
            
            if (clientId) {
                params.socid = clientId;
            }
            
            // Ajouter les paramètres de date
            if (searchDateStart) {
                

                const [year, month, day] = searchDateStart.split('-');
                params.search_dateorder_start_day = parseInt(day);
                params.search_dateorder_start_month = parseInt(month);
                params.search_dateorder_start_year = parseInt(year);
            }
            
            if (searchDateEnd) {
                const [year, month, day] = searchDateEnd.split('-');
                params.search_dateorder_end_day = parseInt(day);
                params.search_dateorder_end_month = parseInt(month);
                params.search_dateorder_end_year = parseInt(year);
            }

            if (searchStatus) {
                params.search_status = searchStatus;
            }

            if (searchProductRef) {
                params.search_product_ref = searchProductRef;
            }


            

            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            const propalsWithClientInfo = response.data.map((propal: any) => ({
                ...propal,
                client_email: propal.email || '',
                client_phone: propal.phone || '',
                client_address: propal.address || ''
            }));

            setPropals(propalsWithClientInfo || []);
            setTotalCount(response.nbtotalofrecords || 0);
            setTotalPages(response.totalpages || 1);
            setCountPerPage(response.count || 0);
        } catch (error) {
            console.error('Error fetching propals:', error);
        } finally {
            setLoading(false);
        }
    };
 

    
const fetchPropalDetails = async (propalRef :string) => {
    setDetailsLoading(true);
    try {
        const response: any = await axios.post(
            'https://api.apapiecesauto.fr/apiV2/',
            {
                action: 'propaldetails',
                entity: user.entity,
                ref: propalRef
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                }
            }
        );
        console.log('Données reçues du backend:', response.data);
        console.log('Lignes du devis:', response.data?.lines);

        if (response.data) {
            // Déterminer le statut selon la source disponible
            // Priorité: response.data.statut -> response.data.fk_statut -> response.statut
            const statut = response.data.fk_statut || response.fk_statut || '';
            
            setPropalDetails({
                ...response.data,
                fk_statut: statut, // Stocker explicitement le statut
                orders: response.orders || [] 
            });
            
            setPropalPdf({
                propalPdf: response.propalPdf || response.orderpdf || '',
                orderpdf: response.orderpdf || ''
            });
            
            setOrderPdf({
                orderpdf: response.orderpdf || ''
            });
            
            setEditedLines([...response.data.lines]);
            setDeletedLineIds([]);
            
            // Débogage (optionnel)
            console.log('Propal statut:', statut);
            console.log('Propal détails:', {
                ...response.data,
                fk_statut: statut,
                orders: response.orders || []
            });
        }
    } catch (error) {
        console.error('Error fetching propal details:', error);
    } finally {
        setDetailsLoading(false);
    }
};
    const handleGenerateOrderFromPropal = async () => {
        if (!propalDetails || !user) return;
        
        setDetailsLoading(true);
        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'createorderfrompropal',
                    entity: user.entity,
                    propal_id: propalDetails.id
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setToastMessage(`Commande créée avec succès ! Référence : ${response.order_ref}`);
                setToastColor('success');
                setToastOpen(true);
                
                setModalOpen(false);
            } else {
                setToastMessage(response.message || 'Erreur lors de la création de la commande');
                setToastColor('danger');
                setToastOpen(true);
            }
        } catch (error: any) {
            console.error('Error generating order from propal:', error);
            setToastMessage(error.response?.data?.message || 'Erreur lors de la création de la commande');
            setToastColor('danger');
            setToastOpen(true);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCancelDeletePropal = async (propalRef: string, propalId: string) => {
        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'cancelpropal',
                    entity: user.entity,
                    propal_id: propalId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setToastMessage(response.message || 'Devis traité avec succès');
                setToastColor('success');
                setToastOpen(true);

                fetchPropals();

                if (modalOpen && propalDetails?.ref === propalRef) {
                    setModalOpen(false);
                }
            } else {
                setToastMessage(response.message || 'Erreur lors du traitement');
                setToastColor('danger');
                setToastOpen(true);
            }
        } catch (error) {
            console.error('Error canceling/deleting propal:', error);
            setToastMessage('Erreur lors du traitement du devis');
            setToastColor('danger');
            setToastOpen(true);
        }
    };

 
    const handleQuantityChange = (index: number, value: string) => {
        const newLines = [...editedLines];
        const qty = parseInt(value) || 0;
        newLines[index].qty = qty.toString();

        const price = parseFloat(newLines[index].subprice) || 0;
        const tvaRate = parseFloat(newLines[index].tva_tx) || 0;
        newLines[index].total_ht = (price * qty).toFixed(2);
        newLines[index].total_tva = (price * qty * tvaRate / 100).toFixed(2);
        newLines[index].total_ttc = (price * qty * (1 + tvaRate / 100)).toFixed(2);

        setEditedLines(newLines);
    };

    const handleToggleDeleteLine = (lineId: string) => {
        setDeletedLineIds(prev =>
            prev.includes(lineId)
                ? prev.filter(id => id !== lineId)
                : [...prev, lineId]
        );
    };

    


    const handleValidateChanges = async () => {
        if (!propalDetails || !user) return;

        try {

            if (deletedLineIds.length > 0) {
                const deleteResponse = await axios.post(
                    'https://api.apapiecesauto.fr/apiV2/',
                    {
                        action: "deletepropalline",
                        entity: user.entity,
                        id: propalDetails.id,
                        line_ids: deletedLineIds
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                );

            }

            if (editedLines.length > 0) {
                const remainingLines = editedLines.filter(line => !deletedLineIds.includes(line.id));

                if (remainingLines.length > 0) {
                    const propalData = {
                        action: "updatepropal",
                        entity: user.entity,
                        id: propalDetails.id,
                        lines: remainingLines.map(line => ({
                            id: line.id,
                            unitprice: parseFloat(line.subprice) || 0,
                            qty: parseInt(line.qty) || 0,
                        }))
                    };


                    const updateResponse = await axios.post(
                        'https://api.apapiecesauto.fr/apiV2/',
                        propalData,
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${user.token}`,
                            }
                        }
                    );

                }
            }

            if (deletedLineIds.length > 0 || editedLines.length > 0) {
                // Show success toast
                setToastMessage('Modifications validées avec succès.');
                setToastColor('success');
                setToastOpen(true);

                // Refresh the order details
                await fetchPropalDetails(propalDetails.ref);
            }

        } catch (error) {
            console.error('Error validating changes:', error);
            setToastMessage('Erreur lors de la validation des modifications.');
            setToastColor('danger');
            setToastOpen(true);
        }

        setIsEditing(false);
        setDeletedLineIds([]);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (propalDetails) {
            setEditedLines([...propalDetails.lines]);
            setDeletedLineIds([]);
        }
    };

    const getStatusBadge = (statusCode: string) => {
        switch (statusCode) {
            case "-1": return { label: "Annulé", color: "danger" };
            case "0": return { label: "Brouillon", color: "warning" };
            case "1": return { label: "Validé", color: "success" };
            case "2": return { label: "Signé", color: "info" };
            case "3": return { label: "Refusé", color: "danger" };
            case "4": return { label: "Facturé", color: "primary" };
            default: return { label: "Inconnu", color: "secondary" };
        }
    };

    const handleViewPropal = async (propalId: string) => {
        const propal = propals.find(o => o.rowid === propalId);
        if (propal) {
            await fetchPropalDetails(propal.ref);
            setModalOpen(true);
        }
    };

    const location = useLocation();

    const searchAndOpenPropalById = async (propalId: string) => {
        setDetailsLoading(true);

        try {
            const directResponse = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'propaldetails',
                    entity: user.entity,
                    id: propalId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (directResponse.data) {
                setPropalDetails(directResponse.data);
                setDetailsLoading(false);
                setModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching propal details by ID:', error);
            setToastMessage('Devis non trouvé.');
            setToastColor('danger');
            setToastOpen(true);
            setDetailsLoading(false);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleEditOrder = () => {
        if (propalDetails) {
            setEditedLines([...propalDetails.lines]);
            setDeletedLineIds([]);
            setIsEditing(true);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchPropals();
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleClientSelect = (client: Client) => {
        setClientId(client.rowid);
        setClientName(client.nom);
        setClientSearchTerm(client.nom);
        setFetchedClients([]);
        setClientDropdownOpen(false);
        setPage(1);
    };

    const handleEnCompteChange = (checked: boolean) => {
        setEnCompte(checked);
        if (checked) {
            setComptoir(false);
            setClientId(null);
            setClientName("");
            setClientSearchTerm("");
            setFetchedClients([]);
        }
        setFetchedClients([]);
        setClientDropdownOpen(false);
    };

    const handleComptoirChange = (checked: boolean) => {
        setComptoir(checked);
        if (checked) {
            setEnCompte(false);
            setClientId("74057");
            setClientName("Client Comptoir");
            setClientSearchTerm("Client Comptoir");
            setFetchedClients([]);
            setClientDropdownOpen(false);
        } else {
            setClientId(null);
            setClientName("");
            setClientSearchTerm("");
        }
    };

    const fetchClients = async () => {
        if (clientSearchTerm.trim().length < 3 || comptoir) {
            setFetchedClients([]);
            return;
        }

        setLoadingClients(true);
        setClientFetchError(null);

        try {
            const response = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "clients",
                    entity: user.entity,
                    search: clientSearchTerm,
                    encompte: enCompte
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            const clients: Client[] = response.data || [];
            setFetchedClients(clients);
        } catch (error) {
            setClientFetchError("Erreur lors de la récupération des clients.");
        } finally {
            setLoadingClients(false);
        }
    };

    const clearAllFilters = () => {
        setSearchRef("");
        setSearchDateStart("");
        setSearchDateEnd("");
        setClientId(null);
        setClientName("");
        setClientSearchTerm("");
        setFetchedClients([]);
        setEnCompte(false);
        setComptoir(false);
        setPage(1);
    };

    const clearClientFilter = () => {
        setClientId(null);
        setClientName("");
        setClientSearchTerm("");
        setFetchedClients([]);
        setEnCompte(false);
        setComptoir(false);
        setPage(1);
    };

    const columns = [
        {
            header: "Référence",
            accessorKey: "ref",
            cell: (cell: any) => (
                <span className="fw-semibold">{cell.getValue()}</span>
            )
        },
        {
            header: "Date Devis",
            accessorKey: "date_propale",
            cell: (cell: any) => (
                <span>{new Date(cell.getValue()).toLocaleDateString()}</span>
            )
        },
        {
            header: "Client",
            accessorKey: "name",
            cell: (cell: any) => (
                <span>{cell.getValue()}</span>
            )
        },
        {
            header: "Total HT",
            accessorKey: "total_ht",
            cell: (cell: any) => (
                <span>{parseFloat(cell.getValue()).toFixed(2)} €</span>
            )
        },
        {
            header: "Total TTC",
            accessorKey: "total_ttc",
            cell: (cell: any) => (
                <span>{parseFloat(cell.getValue()).toFixed(2)} €</span>
            )
        },
        {
            header: "Statut",
            accessorKey: "fk_statut",
            cell: (cell: any) => {
                const status = getStatusBadge(cell.getValue());
                return <Badge color={status.color}>{status.label}</Badge>;
            }
        },
        {
            header: "Action",
            accessorKey: "rowid",
            cell: (cell: any) => {
                const propalRow = cell.row.original;
                const isBilled = propalRow.billed === "1";
                const status = propalRow.fk_statut;
                const isCanceled = status === "-1";

                const shouldShowCancelDelete = !isBilled && (status === "0" || status === "1");

                return (
                    <div className="d-flex justify-content-end gap-2">
                        {shouldShowCancelDelete && (
                            <Button
                                color="danger"
                                size="sm"
                                onClick={() => {
                                    setToastMessage(`Voulez-vous vraiment ${status === "0" ? "supprimer" : "annuler"} ce devis ?`);
                                    setToastColor('warning');
                                    setToastOpen(true);
                                    setConfirmAction(() => () => handleCancelDeletePropal(propalRow.ref, propalRow.rowid));
                                }}
                                disabled={loading}
                                className="px-2"
                            >
                                <i className={`ri-${status === "0" ? "delete-bin-line" : "close-circle-line"}`}></i> {status === "0" ? "Supprimer" : "Annuler"}
                            </Button>
                        )}
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleViewPropal(propalRow.rowid)}
                            disabled={loading}
                            className="px-2"
                        >
                            <i className="ri-eye-line"></i> Voir
                        </Button>
                    </div>
                );
            },
            meta: {
                headerClass: "text-center",
                cellClass: "text-center",
                style: {
                    textAlign: "center"
                }
            }
        }
    ];

    useEffect(() => {
        if (location.state?.openPropalModal && location.state?.propalId) {
            searchAndOpenPropalById(location.state.propalId);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    useEffect(() => {
        fetchPropals();
    }, [page, clientId, searchRef, searchDateStart, searchDateEnd,searchProduct,searchStatus,searchProductRef,user.entity]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (clientSearchTerm.trim().length >= 3 && !comptoir) {
                fetchClients();
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [clientSearchTerm, enCompte, comptoir]);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between mb-3">
                                    <h4 className="card-title mb-0">Liste des Devis</h4>
                                    <div className="text-muted">
                                        Total: <strong>{totalCount}</strong> devis
                                    </div>
                                </div>

                                <form onSubmit={handleSearch} className="mb-4">
                                    <Row className="g-3 align-items-end">
                                    <Col md={2}>
                                    <FormGroup className="mb-0">
        <Label className="form-label fw-semibold small text-muted mb-1">
            DATE DEBUT - FIN
        </Label>
        <InputGroup>
            <InputGroupText className="bg-light border-end-0">
                <i className="ri-calendar-line"></i>
            </InputGroupText>
            
            <Flatpickr
                className="form-control border-start-0"
                options={{
                    mode: "range",
                    allowInput: true,
                    onReady: (selectedDates, dateStr, instance) => {
                    instance.set("mode", "range");
                }
                }}
               
                onChange={(dates) => {
                if (dates.length === 1) {
                    // Si un seul jour est sélectionné, le mettre comme date de début et de fin
                    setSearchDateStart(dates[0].toISOString().slice(0, 10));
                    setSearchDateEnd(dates[0].toISOString().slice(0, 10));
                } else if (dates.length === 2) {
                    // Si deux dates sont sélectionnées
                    // Correction du décalage de fuseau horaire
                    const date1 = new Date(dates[0]);
                    const date2 = new Date(dates[1]);
                    
                    // Ajuster pour obtenir la date correcte (sans décalage de fuseau horaire)
                    const adjustedDate1 = new Date(date1.getTime() + Math.abs(date1.getTimezoneOffset()) * 60000);
                    const adjustedDate2 = new Date(date2.getTime() + Math.abs(date2.getTimezoneOffset()) * 60000);
                    
                    setSearchDateStart(adjustedDate1.toISOString().slice(0, 10));
                    setSearchDateEnd(adjustedDate2.toISOString().slice(0, 10));
                } else {
                    setSearchDateStart("");
                    setSearchDateEnd("");
                }
            }}
                style={{ height: "42px" }}
                value={searchDateStart && searchDateEnd ? `${searchDateStart} to ${searchDateEnd}` : ""}
            />
            
           
             {(searchDateStart || searchDateEnd) && (
            <span 
                className="input-group-text bg-light border-start-0"
                style={{ 
                    cursor: "pointer",
                    height: "42px"
                }}
                onClick={() => {
                    setSearchDateStart("");
                    setSearchDateEnd("");
                }}
            >
                <i className="ri-close-line text-danger"></i>
            </span>
        )}
    
        </InputGroup>
                                   </FormGroup> 
    
                                    </Col>

                                        <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label for="reference" className="form-label fw-semibold small text-muted mb-1">
                                                    RÉFÉRENCE
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-barcode-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        id="reference"
                                                        type="text"
                                                        placeholder="Référence..."
                                                        value={searchRef}
                                                        onChange={(e) =>{ setSearchRef(e.target.value);}}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                         <Col md={2}>
                                            <FormGroup className="mb-0">
                                                <Label for="reference" className="form-label fw-semibold small text-muted mb-1">
                                                    RÉFÉRENCE PRODUCT
                                                </Label>
                                                <InputGroup>
                                                    <InputGroupText className="bg-light border-end-0">
                                                        <i className="ri-shopping-bag-line"></i>
                                                    </InputGroupText>
                                                    <Input
                                                        id="product_reference"
                                                        type="text"
                                                        placeholder="Référence..."
                                                        value={searchProductRef}
                                                        onChange={(e) =>{ setSearchProductRef(e.target.value)}}
                                                        className="border-start-0"
                                                        style={{ height: "42px" }}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>

                                        <Col md={2}>
                                        <FormGroup className="mb-0">
        <Label for="statut" className="form-label fw-semibold small text-muted mb-1">
            STATUT
        </Label>
        <InputGroup>
            <InputGroupText className="bg-light border-end-0">
                <i className="ri-file-text-line"></i>
            </InputGroupText>
            <Input
                id="statut"
                type="select"
                placeholder="Statut..."
                value={searchStatus}
                onChange={(e) => setSearchStatus(e.target.value)}
                className="border-start-0"
                style={{ height: "42px" }}
            >
                <option value="">Tous les statuts</option>
                <option value="0">Brouillon</option>
                <option value="1">Validé</option>
                <option value="2">Signé</option>
                <option value="3">Non signé</option>
                <option value="4">Facturé</option>
                
                <i className="ri-close-line"></i>
            </Input>
            
        </InputGroup>
                                        </FormGroup>
                                        </Col>
                                        <Col md={3} >
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1">
                                                    CLIENT
                                                </Label>

                                                <div className="d-flex gap-2 align-items-center">
                                                    <div className="d-flex flex-column gap-1 me-2">
                                                        <FormGroup check className="mb-0">
                                                            <Input
                                                                type="checkbox"
                                                                id="enCompte"
                                                                checked={enCompte}
                                                                onChange={(e) => handleEnCompteChange(e.target.checked)}
                                                                className="form-check-input"
                                                            />
                                                            <Label for="enCompte" check className="form-check-label small fw-medium" style={{whiteSpace: "nowrap"}}>
                                                                En Compte
                                                            </Label>
                                                        </FormGroup>

                                                        <FormGroup check className="mb-0">
                                                            <Input
                                                                type="checkbox"
                                                                id="comptoir"
                                                                checked={comptoir}
                                                                onChange={(e) => handleComptoirChange(e.target.checked)}
                                                                className="form-check-input"
                                                            />
                                                            <Label for="comptoir" check className="form-check-label small fw-medium">
                                                                Comptoir
                                                            </Label>
                                                        </FormGroup>
                                                    </div>

                                                    <div className="position-relative flex-grow-1">
                                                        <InputGroup>
                                                            <InputGroupText className="bg-light border-end-0">
                                                                <i className="ri-user-line"></i>
                                                            </InputGroupText>
                                                            <Input
                                                                type="text"
                                                                placeholder={comptoir ? "Client Comptoir" : "Rechercher un client..."}
                                                                value={comptoir ? "Client Comptoir" : clientSearchTerm}
                                                                onChange={(e) => {
                                                                    if (!comptoir) {
                                                                        setClientSearchTerm(e.target.value);
                                                                        if (e.target.value.length >= 3) {
                                                                            setClientDropdownOpen(true);
                                                                        } else {
                                                                            setClientDropdownOpen(false);
                                                                        }
                                                                    }
                                                                }}
                                                                onFocus={() => {
                                                                    if (!comptoir && clientSearchTerm.length >= 3) {
                                                                        setClientDropdownOpen(true);
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    setTimeout(() => setClientDropdownOpen(false), 200);
                                                                }}
                                                                disabled={comptoir}
                                                                className="border-start-0"
                                                                style={{ height: "42px" }}
                                                            />
                                                            {clientName && (
                                                                <Button
                                                                    color="light"
                                                                    onClick={clearClientFilter}
                                                                    className="border-0 text-muted"
                                                                    style={{ height: "42px" }}
                                                                    type="button"
                                                                >
                                                                    <i className="ri-close-line"></i>
                                                                </Button>
                                                            )}
                                                        </InputGroup>

                                                        {clientDropdownOpen && !comptoir && fetchedClients.length > 0 && (
                                                            <div
                                                                className="position-absolute w-100 border shadow-lg bg-white mt-1 rounded"
                                                                style={{
                                                                    zIndex: 1000,
                                                                    maxHeight: "300px",
                                                                    overflowY: "auto",
                                                                    top: "100%",
                                                                    left: 0
                                                                }}
                                                            >
                                                                <div className="p-2">
                                                                    {loadingClients && (
                                                                        <div className="text-center py-2">
                                                                            <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                                            <span className="text-muted">Recherche en cours...</span>
                                                                        </div>
                                                                    )}

                                                                    {clientFetchError && (
                                                                        <div className="alert alert-danger small mb-2 py-2">
                                                                            <i className="ri-error-warning-line me-1"></i>
                                                                            {clientFetchError}
                                                                        </div>
                                                                    )}

                                                                                                            {fetchedClients.length === 0 && clientSearchTerm.length >= 3 && !loadingClients && (
                                                                                                                <div className="text-center text-muted py-3">
                                                                                                                    <i className="ri-user-search-line d-block mb-1"></i>
                                                                                                                    Aucun client trouvé
                                                                                                                </div>
                                                                                                            )}

                                                                    {fetchedClients.map((client) => (
                                                                        <div
                                                                            key={client.rowid}
                                                                            onClick={() => {
                                                                                handleClientSelect(client);
                                                                                setClientDropdownOpen(false);
                                                                            }}
                                                                            className="py-2 px-3 border-bottom cursor-pointer"
                                                                            style={{
                                                                                whiteSpace: "normal",
                                                                                cursor: "pointer",
                                                                                transition: "background-color 0.2s"
                                                                            }}
                                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                                                        >
                                                                            <div className="d-flex justify-content-between align-items-start">
                                                                                <div className="flex-grow-1">
                                                                                    <div className="fw-semibold text-primary mb-1">
                                                                                        {client.nom}
                                                                                    </div>
                                                                                    {client.phone && (
                                                                                        <div className="small text-muted mb-1">
                                                                                            <i className="ri-phone-line me-1"></i>
                                                                                            {client.phone}
                                                                                        </div>
                                                                                    )}
                                                                                    {client.email && (
                                                                                        <div className="small text-muted text-truncate">
                                                                                            <i className="ri-mail-line me-1"></i>
                                                                                            {client.email}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <Badge color="light" className="ms-2 text-muted small">
                                                                                    {client.rowid}
                                                                                </Badge>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </Col>



                                        <Col md={1} className="">
                                            <FormGroup className="mb-0">
                                                <Label className="form-label fw-semibold small text-muted mb-1 d-block">
                                                    &nbsp;
                                                </Label>
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    className="w-100"
                                                    style={{ height: "42px" ,whiteSpace: "nowrap",paddingLeft:"5px",paddingRight:"10px"}}
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        <>
                                                            <i className="ri-search-line align-bottom me-1" ></i>
                                                            Rechercher
                                                        </>
                                                    )}
                                                </Button>
                                            </FormGroup>
                                        </Col>

                                        
                                    </Row>
                                </form>

                                

                                {clientName && (
                                    <div className="alert alert-info mb-3 py-2">
                                        <small>
                                            <i className="ri-information-line me-1"></i>
                                            Filtrage par client: <strong>{clientName}</strong>
                                        </small>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={clearClientFilter}
                                            className="float-end p-0"
                                        >
                                            <small>Effacer</small>
                                        </Button>
                                    </div>
                                )}

                                {(searchDateStart || searchDateEnd) && (
                                    <div className="alert alert-info mb-3 py-2">
                                        <small>
                                            <i className="ri-calendar-line me-1"></i>
                                            Filtrage par date: 
                                            {searchDateStart && <span> Du {new Date(searchDateStart).toLocaleDateString()}</span>}
                                            {searchDateEnd && <span> au {new Date(searchDateEnd).toLocaleDateString()}</span>}
                                        </small>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => {
                                                setSearchDateStart("");
                                                setSearchDateEnd("");
                                                setPage(1);
                                                clearAllFilters();
                                            }}
                                            className="float-end p-0"
                                        >
                                            <small
                                            onClick={() => {
                                                
                                            clearAllFilters();}}>Effacer</small>
                                        </Button>
                                    </div>
                                )}

                                {loading && propals.length === 0 ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" />
                                        <div className="mt-2">Chargement des devis...</div>
                                    </div>
                                ) : (
                                    <>
                                        {propals.length === 0 ? (
                                            <div className="text-center py-4">
                                                <i className="ri-file-search-line display-5 text-muted"></i>
                                                <h5 className="mt-3">Aucun devis trouvé</h5>
                                                <p className="text-muted">
                                                    {searchRef || searchDateStart || searchDateEnd || clientId
                                                        ? "Essayez avec d'autres critères de recherche"
                                                        : "Il n'y a aucun devis à afficher"}
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="position-relative">
                                                    {loading && (
                                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 10 }}>
                                                            <div className="text-center">
                                                                <Spinner color="primary" />
                                                                <div className="mt-2">Chargement...</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <TableContainer
                                                        columns={columns}
                                                        data={propals}
                                                        isGlobalFilter={false}
                                                        customPageSize={100}
                                                        divClass="table-responsive"
                                                        tableClass="align-middle table-nowrap"
                                                        theadClass="table-light text-muted"
                                                    />
                                                </div>

                                                {propals.length > 0 && (
                                                    <SmartPagination
                                                        currentPage={page}
                                                        totalPages={totalPages}
                                                        totalCount={totalCount}
                                                        countPerPage={countPerPage}
                                                        onPageChange={handlePageChange}
                                                        loading={loading}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                     
                </Row>

            </Container>

            <Modal isOpen={modalOpen} toggle={() => { setModalOpen(false); setIsEditing(false); }} size="xl">
                <ModalHeader toggle={() => {
                    setModalOpen(false);
                    setIsEditing(false);
                }} className="d-flex justify-content-between align-items-center">
                   <div>
    Devis {propalDetails?.ref} 
    {propalDetails && (
        <Badge color={getStatusBadge(propalDetails?.fk_statut?.toString()).color} className="ms-2">
            {getStatusBadge(propalDetails?.fk_statut?.toString()).label}
        </Badge>
    )}
</div>
                </ModalHeader>
                <ModalBody>
                    {detailsLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <div className="mt-2">Chargement des détails...</div>
                        </div>
                    ) : propalDetails ? (
                        <div className="order-details-container">
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="p-3 border rounded mb-3">
                                        <h6>Informations client</h6>
                                        <div className="mb-2">
                                            <strong>Client:</strong> {propalDetails.thirdparty.name}
                                        </div>
                                        {propalDetails.thirdparty.email && (
                                            <div className="mb-2">
                                                <strong>Email:</strong> {propalDetails.thirdparty.email}
                                            </div>
                                        )}
                                        {propalDetails.thirdparty.phone && (
                                            <div className="mb-2">
                                                <strong>Téléphone:</strong> {propalDetails.thirdparty.phone}
                                            </div>
                                        )}
                                        {propalDetails.thirdparty.address && (
                                            <div className="mb-2">
                                                <strong>Adresse:</strong> {propalDetails.thirdparty.address}
                                            </div>
                                        )}
                                        <div>
                                            <strong>Date devis:</strong> {new Date(propalDetails.datep * 1000).toLocaleDateString()}
                                        </div>

                                        {propalDetails?.orders && propalDetails.orders.length > 0 && propalDetails.orders[0]?.ref && (
                                            <div className="mb-2">
                                                <strong>Commandes :</strong> {propalDetails.orders[0].ref}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="p-3 border rounded mb-3">
                                        <h6>Résumé financier</h6>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Total HT:</span>
                                            <span>{parseFloat(propalDetails.total_ht).toFixed(2)} €</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Total TTC:</span>
                                            <span>{parseFloat(propalDetails.total_ttc).toFixed(2)} €</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <Table striped bordered hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Référence</th>
                                            <th>Produit</th>
                                            <th className="text-end">Quantité</th>
                                            <th className="text-end">Prix unitaire</th>
                                            <th className="text-end">Total HT</th>
                                            <th className="text-end">Total TTC</th>
                                            {isEditing && <th style={tableStyles.actionCell}>Action</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(isEditing ? editedLines : propalDetails?.lines).map((line, index) => (
                                            <tr key={index} style={isEditing && deletedLineIds.includes(line.id) ? { backgroundColor: '#FEF2F2' } : {}}>
                                                <td>{line.product_ref}</td>
                                                <td style={{ maxWidth: '400px' }}>{line.product_label || ''}</td>
                                                <td className="text-end" style={tableStyles.quantityCell}>
                                                    {isEditing ? (
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={line.qty}
                                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                            style={tableStyles.quantityInput}
                                                            className="text-end"
                                                        />
                                                    ) : (
                                                        line.qty
                                                    )}
                                                </td>
                                                <td className="text-end">{parseFloat(line.subprice).toFixed(2)} €</td>
                                                <td className="text-end">{parseFloat(line.total_ht).toFixed(2)} €</td>
                                                <td className="text-end">{parseFloat(line.total_ttc).toFixed(2)} €</td>
                                                {isEditing && (
                                                    <td style={tableStyles.actionCell}>
                                                        <Button
                                                            color={deletedLineIds.includes(line.id) ? 'success' : 'danger'}
                                                            size="sm"
                                                            style={tableStyles.deleteButton}
                                                            onClick={() => handleToggleDeleteLine(line.id)}
                                                        >
                                                            <i className={deletedLineIds.includes(line.id) ? 'ri-arrow-go-back-line' : 'ri-close-line'}></i>
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                  
                                </Table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-danger"></i>
                            <h5 className="mt-3">Détails non disponibles</h5>
                        </div>
                    )}
                </ModalBody>
                
                <ModalFooter>
                    {isEditing ? (
                        <>
                            <Button color="secondary" onClick={handleCancelEdit}>
                                Annuler
                            </Button>
                            <Button color="primary" onClick={handleValidateChanges} disabled={detailsLoading}>
                                {detailsLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Création...
                                    </>
                                ) : (
                                    'Valider'
                                )}
                            </Button>
                        </>
                    ) : (
                        <div>
                            {propalPdf?.propalPdf &&  (
                                <Button
                                    color="info"
                                    size="md"
                                    className="me-2"
                                    onClick={() => {
                                        setPdfType('propal');
                                        setPdfModalOpen(true);
                                    }}
                                >
                                    <i className="ri-file-text-line align-bottom me-2"></i> Voir PDF Devis 
                                </Button>
                            )}

                            {orderPdf?.orderpdf && propalDetails?.orders && propalDetails.orders.length > 0 && (
                                <Button
                                    color="danger"
                                    size="md"
                                    className="me-2"
                                    onClick={() => {
                                        setPdfType('commande');
                                        setPdfModalOpen(true);
                                    }}
                                >
                                    <i className="ri-file-pdf-line align-bottom me-2"></i> Voir PDF Commande
                                </Button>
                            )}

                            {(() => {
                                const currentPropal = propals.find(o => o.ref === propalDetails?.ref);
                                const isBilled = currentPropal?.billed === "1";
                                const status = propalDetails?.fk_statut;
                                const isCanceled = status === "-1";

                                const shouldShowCancelDelete = !isBilled && (status === "0" || status === "1");

                                return (
                                    <>
                                        {shouldShowCancelDelete && (
                                            <Button
                                                color="danger"
                                                size="md"
                                                className="me-2"
                                                onClick={() => {
                                                    setToastMessage(`Voulez-vous vraiment ${status === "0" ? "supprimer" : "annuler"} ce devis ?`);
                                                    setToastColor('warning');
                                                    setToastOpen(true);
                                                    setConfirmAction(() => () => {
                                                        if (propalDetails) {
                                                            handleCancelDeletePropal(propalDetails.ref, propalDetails.id);
                                                            setModalOpen(false);
                                                        }
                                                    });
                                                }}
                                            >
                                                <i className={`ri-${status === "0" ? "delete-bin-line" : "close-circle-line"} align-bottom me-1`}></i>
                                                {status === "0" ? "Supprimer" : "Annuler"}
                                            </Button>
                                        )}

                                        {!isBilled && !isCanceled && (
                                            <>
                                                <Button
                                                    color="success"
                                                    size="md"
                                                    className="me-2"
                                                    onClick={handleGenerateOrderFromPropal}
                                                    disabled={detailsLoading}
                                                >
                                                    {detailsLoading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" />
                                                            Création...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-file-copy-line align-bottom me-1"></i>
                                                            Créer une commande
                                                        </>
                                                    )}
                                                </Button>

                                                <Button
                                                    color="primary"
                                                    size="md"
                                                    className="me-2"
                                                    onClick={handleEditOrder}
                                                >
                                                    <i className="ri-edit-line align-bottom me-1"></i> Modifier
                                                </Button>

                                                
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </ModalFooter>
            </Modal>


            <Modal 
                isOpen={pdfModalOpen && pdfType === 'commande'} 
                toggle={() => setPdfModalOpen(false)}
                size="xl" 
                style={{ maxWidth: '90vw' }}
            >
                <ModalHeader toggle={() => setPdfModalOpen(false)}>
                    <div>
                        <i className="ri-file-pdf-line me-2 text-danger"></i>
                        Commande {orderPdf && ``} - {propalDetails?.ref}
                    </div>
                </ModalHeader>
                <ModalBody style={{ height: '80vh', padding: '0' }}>
                    {orderPdf?.orderpdf ? (
                        <iframe
                            src={orderPdf.orderpdf}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title={`Commande ${propalDetails?.ref}`}
                        />
                        
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-muted"></i>
                            <h5 className="mt-3">PDF de commande non disponible</h5>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            <Modal 
                isOpen={pdfModalOpen && pdfType === 'propal'} 
                toggle={() => setPdfModalOpen(false)}
                size="xl" 
                style={{ maxWidth: '90vw' }}
            >
                <ModalHeader toggle={() => setPdfModalOpen(false)}>
                    <div>
                        <i className="ri-file-text-line me-2 text-info"></i>
                        Devis {propalPdf && ``} - {propalDetails?.ref}
                    </div>
                </ModalHeader>
                <ModalBody style={{ height: '80vh', padding: '0' }}>
                    {propalPdf?.propalPdf ? (
                        <iframe
                            src={propalPdf.propalPdf}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title={`Devis ${propalDetails?.ref}`}
                        />
                    ) : (
                        <div className="text-center py-4">
                            <i className="ri-error-warning-line display-5 text-muted"></i>
                            <h5 className="mt-3">PDF de devis non disponible</h5>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            {toastOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{ width: '400px', maxWidth: '90vw' }}>
                        <Toast isOpen={toastOpen} className={`bg-white text-dark`}>
                            <ToastHeader
                                toggle={() => {
                                    setToastOpen(false);
                                    setConfirmAction(null);
                                }}
                                className={`bg-white text-dark d-flex justify-content-between align-items-center`}
                            >
                                <span>
                                    <i className={`me-2 ${toastColor === 'warning' ? 'ri-information-line' : toastColor === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
                                    {toastColor === 'warning' ? 'Confirmation' : toastColor === 'success' ? 'Succès' : 'Erreur'}
                                </span>
                            </ToastHeader>
                            <ToastBody>
                                <div className="d-flex flex-column">
                                    <p className="mb-3 text-center">{toastMessage}</p>
                                    {toastColor === 'warning' && confirmAction && (
                                        <div className="d-flex gap-2 justify-content-center">
                                            <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => {
                                                    setToastOpen(false);
                                                    setConfirmAction(null);
                                                }}
                                                className="flex-fill"
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => {
                                                    if (confirmAction) {
                                                        confirmAction();
                                                    }
                                                    setToastOpen(false);
                                                    setConfirmAction(null);
                                                }}
                                                className="flex-fill"
                                            >
                                                Confirmer
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </ToastBody>
                        </Toast>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropalsPage;
